/** Glossary of engineering / AMI / reliability jargon (UX spec §3.2, finding
 *  B1). Rendered by components/common/Term.tsx as a dotted-underline tooltip.
 *  Register: plain distribution-ops English, 1-2 sentences per definition.
 *  Content files are authored in the plan and reviewed at PR time (D-11). */

export interface GlossaryEntry { term: string; def: string }

export const GLOSSARY: Record<string, GlossaryEntry> = {
  slg: {
    term: 'SLG',
    def: 'Single line-to-ground fault: one phase conductor touching ground (a downed line, a tree limb). The most common fault type, and usually the current a fuse on a single-phase tap actually sees.',
  },
  xr: {
    term: 'X/R',
    def: 'How inductive the circuit looks at the fault point (reactance over resistance). A higher X/R makes the first cycles of fault current spike harder, which is what stresses interrupting devices.',
  },
  asym: {
    term: 'asymmetrical',
    def: 'Fault current during the first few cycles, while a temporary DC offset is still dying out. It runs higher than the settled (symmetrical) value and is what a device must actually survive.',
  },
  pu: {
    term: 'pu',
    def: 'Per-unit: voltage as a fraction of nominal. 1.00 pu is exactly nameplate voltage and 0.95 pu is 5% low, which puts 120 V and 7,200 V points on the same scale.',
  },
  tcc: {
    term: 'TCC',
    def: 'Time-current curve: how fast a protective device operates at each level of fault current. Two devices coordinate when the downstream curve always operates faster than the upstream one.',
  },
  ppe: {
    term: 'PPE',
    def: 'Arc-rated protective clothing category (1-4, per NFPA 70E) required to work a location energized. Higher incident energy means a higher category; DANGEROUS means do not work it energized.',
  },
  calcm2: {
    term: 'cal/cm²',
    def: 'Calories per square centimeter: the heat an arc flash would deliver at working distance. About 1.2 cal/cm² causes a second-degree burn; protective clothing is rated in the same unit.',
  },
  kvar: {
    term: 'kVAR',
    def: 'Reactive power: current that supports voltage and magnetic fields but does no billable work. It still heats conductors and fills transformer capacity, which is why power factor matters.',
  },
  kva: {
    term: 'kVA',
    def: 'Apparent power (kilovolt-amperes), and the nameplate size of a transformer. Loading percentages compare the demand a transformer serves against this rating.',
  },
  interrupting: {
    term: 'interrupting rating',
    def: 'The most fault current a fuse, recloser, or breaker can safely clear. If the available fault current is higher, the device can rupture instead of clearing the fault.',
  },
  coincident: {
    term: 'coincident peak',
    def: "The combined demand of all meters at the single worst moment, which is what the transformer actually feels. Smaller than adding up each meter’s individual peak, because peaks do not all line up.",
  },
  saidi: {
    term: 'SAIDI',
    def: 'Average outage minutes per customer served over the period: total customer-minutes interrupted divided by customers served. The headline "how long were people out" number.',
  },
  saifi: {
    term: 'SAIFI',
    def: 'Average number of sustained interruptions per customer served over the period. 1.0 means the average customer saw one outage.',
  },
  caidi: {
    term: 'CAIDI',
    def: 'Average minutes to restore service once an outage happens (SAIDI divided by SAIFI). High CAIDI means outages run long, even if they are rare.',
  },
  cmi: {
    term: 'CMI',
    def: 'Customer-minutes interrupted: customers out multiplied by minutes out, summed across events. The raw volume of outage pain before it is averaged into SAIDI.',
  },
  'sag-swell': {
    term: 'sag/swell',
    def: 'Momentary voltage dips (sags) and rises (swells) lasting seconds or less, from motor starts, faults clearing, or switching. Counted as events, separate from sustained voltage-band violations.',
  },
  'phase-balance': {
    term: 'phase balance',
    def: "How evenly a feeder’s load splits across its A, B, and C phases. A heavily loaded phase wastes feeder capacity and pulls its voltage down relative to the others.",
  },
  'last-gasp': {
    term: 'last gasp',
    def: 'The final "power lost" message a meter transmits as it loses power. A burst of last gasps from one area is how an outage is detected before anyone calls it in.',
  },
}
